<template>
  <b-modal :active.sync="opened" :full-screen="isFullScreen" width="500px">
    <div class="card">
      <div class="card-content tw-p-6">
        <validation-observer
          @submit.prevent="post"
          ref="post"
          tag="form"
          v-slot="{ valid }"
        >
          <div class="columns">
            <div class="column is-narrow tw-pr-0">
              <figure>
                <p class="image is-48x48">
                  <img
                    class="is-elongated"
                    v-lazy="user.avatar_thumb_url + '?ts=' + auth.timestamp"
                  />
                </p>
              </figure>
            </div>
            <div class="column">
              <validation-provider
                vid="post"
                rules="required|min:50|max:1500"
                name="post"
                v-slot="{ errors }"
              >
                <b-field
                  :message="errors"
                  :type="{ 'is-danger': errors.length }"
                  class="is-marginless"
                >
                  <quill-editor
                    :placeholder="`Lets share your idea, ${user.first_name}!`"
                    class="quill-post"
                    :class="{ 'has-error': errors.length }"
                    v-model="form.post"
                  />
                </b-field>
              </validation-provider>
            </div>
          </div>

          <div v-if="attachedImage" class="column tw-px-0">
            <figure class="image is-48x48 tw-mt-2">
              <img class="tw-rounded" v-lazy="attachedImage" />
            </figure>
          </div>

          <div class="columns tw-mb-0">
            <div class="column is-4">
              <b-upload
                accept="image/*"
                @input="onPhotoSelect"
                class="tw-text-lg"
              >
                <a class="tw-p-0 tw-flex tw-items-center tw-self-center">
                  <b-icon icon="image-outline" />
                  <span class="tw-ml-2">
                    {{ attachedImage ? 'Change' : 'Add' }} Image
                  </span>
                </a>
              </b-upload>
            </div>
            <div class="column" v-for="option in options" :key="option.value">
              <label
                class="tw-cursor-pointer tw-block tw-text-center tw-rounded-full"
                :class="
                  form.post_type === option.value
                    ? option.color + ' tw-text-white'
                    : 'tw-border-solid tw-border tw-border-gray-100 tw-bg-white-100'
                "
              >
                <input
                  type="radio"
                  v-model="form.post_type"
                  :value="option.value"
                  hidden
                />
                <span>{{ option.text }}</span>
              </label>
            </div>
          </div>

          <hr class="divider tw-mt-4" />

          <div class="level">
            <div class="level-left">
              <b-checkbox v-if="shareToGroup" v-model="form.share">
                <span class="tw-text-base">
                  Share my post on Student Hub
                </span>
              </b-checkbox>
              <b-checkbox v-else v-model="form.share">
                <span class="tw-text-base">
                  Share my post on my Group Scrapbook
                </span>
              </b-checkbox>
            </div>
            <div class="level-right">
              <b-button
                :disabled="!valid || submitting"
                :loading="submitting"
                native-type="submit"
                type="is-primary"
              >
                Share Post
              </b-button>
            </div>
          </div>
        </validation-observer>
      </div>
    </div>

    <!-- Cropper modal -->
    <cropper
      ref="cropper"
      :aspect-ratio="4 / 3"
      :initial-aspect-ratio="4 / 3"
      @change="onPhotoCropped"
    />
  </b-modal>
</template>

<script>
import { mapState } from 'vuex'
import ModalMixin from '@/utils/mixins/modal'
import QuillEditor from '@/components/QuillEditor'
import Cropper from '@/components/modals/Cropper'
import * as PostType from '@/utils/consts/PostType'
import UploadMixin from '@/utils/mixins/upload'

export default {
  mixins: [ModalMixin, UploadMixin],
  props: {
    postTo: {
      type: String,
      default: 'STUDENT_HUB'
    }
  },
  components: {
    QuillEditor,
    Cropper
  },
  data() {
    return {
      opened: false,
      submitting: false,
      options: [
        { value: PostType.PRAYER, text: 'Prayer', color: 'tw-bg-post-prayer' },
        { value: PostType.STORY, text: 'Story', color: 'tw-bg-post-story' },
        { value: PostType.IDEA, text: 'Idea', color: 'tw-bg-post-idea' }
      ],
      form: {
        post: '',
        post_type: PostType.PRAYER,
        image: null,
        share: false
      },
      attachedImage: null
    }
  },
  computed: {
    ...mapState({
      auth: state => state.auth,
      user: state => state.auth.user,
      group: state => state.group.currentGroup
    }),
    shareToGroup() {
      return this.postTo === 'LTG'
    }
  },
  methods: {
    open() {
      this.form = {
        post: '',
        post_type: PostType.PRAYER,
        image: null,
        share: false
      }
      this.opened = true
    },
    onPhotoSelect(file) {
      if (this.validateFileSize(file)) {
        this.photoFileName = file.name
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = e => {
          this.$refs.cropper.open(e.target.result)
        }
      }
    },
    onPhotoCropped(file) {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = e => {
        this.attachedImage = reader.result
        this.form.image = file
      }
    },
    post() {
      this.submitting = true
      const formData = new FormData()
      formData.append('post_to', this.postTo)
      formData.append('post_type', this.form.post_type)
      formData.append('post', this.form.post)
      formData.append('share', this.form.share)
      if (this.form.image) {
        formData.append('image', this.form.image)
      }

      this.$api
        .post('/posts', formData)
        .then(({ data }) => {
          this.$emit('posted', data.data)
          this.opened = false
        })
        .catch(() => {})
        .then(() => {
          this.attachedImage = ''
          this.submitting = false
        })
    }
  }
}
</script>
